import axios, { AxiosResponse } from 'axios';

// Type definitions for WordPress API responses
export interface WordPressPost {
  id: number;
  slug?: string;
  date?: string;
  modified?: string;
  menu_order?: number;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  featured_media: number;
  featured_image_url?: string;
  // NewSiteCMS slider fields (added via register_rest_field)
  use_as_homepage_slider?: 'yes' | 'no';
  homepage_slider_order?: number;
  homepage_slider_image_id?: number;
  homepage_slider_image_url?: string | null;
  acf?: {
    [key: string]: any;
    speaker_type_featured_on_homepage_or_standard?: 'homepage_speaker' | 'standard_speaker';
    biography?: string;
    company?: string;
    topic?: string | string[]; // Support both string (legacy) and array (multi-select)
    fee_range?: string;
    location?: string;
    speaker_image?: {
      url: string;
      alt: string;
    };
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
      alt_text: string;
    }>;
  };
  // Added for internal linking
  link?: string;
  // Added for compatibility with transformSpeaker
  guid?: {
    rendered: string;
  };
}

export interface Speaker extends WordPressPost {
  // Speaker-specific properties can be added here
}

// Get API URL from environment variables
const API_URL: string | undefined = process.env.REACT_APP_API_URL;
const API_TOKEN: string | undefined = process.env.REACT_APP_API_TOKEN;

// Dynamic check for react-snap environment
function isPrerender(): boolean {
  if (typeof window === 'undefined') return false;
  
  const isSnap = 
    (window as any).__REACT_SNAP__ === true ||
    !!(window as any).snapSaveState ||
    (navigator.userAgent || '').includes('ReactSnap') ||
    (navigator.userAgent || '').includes('HeadlessChrome');

  return isSnap;
}

// Log environment status once on load
if (typeof window !== 'undefined') {
  // Log as string to avoid JSHandle@object in puppeteer output
  console.log(`[System] Environment check: isPrerender=${isPrerender()}, apiUrlConfigured=${!!API_URL}, userAgent=${navigator.userAgent}`);
}

// Load local JSON only during react-snap
async function loadPrerenderJson<T>(name: string): Promise<T> {
  try {
    // During prerender, we fetch relative to the root
    const res = await fetch(`/prerender/${name}.json`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`Missing prerender/${name}.json (status: ${res.status})`);
    return res.json();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.error(`[Prerender] Failed to load local JSON for ${name}: ${msg}`);
    return [] as any;
  }
}

// Throw an error if the API URL is not set in the environment variables
// (Allow react-snap to function using prerender json even if env is missing)
if (!API_URL && !isPrerender()) {
  console.warn('REACT_APP_API_URL is not defined. This may cause issues outside of prerendering.');
}

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_URL || 'http://localhost',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add Authorization header if token is present
if (API_TOKEN) {
  const authValue = API_TOKEN.startsWith('Basic ') || API_TOKEN.startsWith('Bearer ') 
      ? API_TOKEN 
      : `Bearer ${API_TOKEN}`;
  apiClient.defaults.headers.common['Authorization'] = authValue;
}

// Pagination interface
export interface PaginationParams {
  page?: number;
  perPage?: number;
}

// Filter interface for server-side filtering
export interface FilterParams {
  search?: string;
  company?: string;
  topic?: string;
}

// Combined params interface
export interface SpeakersQueryParams extends PaginationParams, FilterParams {}

export interface PaginatedResponse<T> {
  data: T[];
  totalPages: number;
  totalItems: number;
  currentPage: number;
  perPage: number;
}

// Lightweight speaker metadata for search/filtering
export interface SpeakerMetadata {
  id: number;
  name: string;
  company: string;
  topic: string | string[]; // Support both string (legacy) and array (multi-select)
  slug?: string;
}

/**
 * Helper: normalize topic into array of trimmed strings
 */
function normalizeTopic(topic: string | string[] | undefined): string[] {
  if (!topic) return [];
  if (Array.isArray(topic)) return topic.map((t) => String(t).trim()).filter(Boolean);
  return String(topic)
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
}

/**
 * Helper: simple includes-match for topic filter
 */
function topicMatches(speakerTopic: string | string[] | undefined, queryTopic: string): boolean {
  if (!queryTopic) return true;
  const q = queryTopic.toLowerCase().trim();
  if (!q) return true;
  const topics = normalizeTopic(speakerTopic).map((t) => t.toLowerCase());
  return topics.some((t) => t.includes(q));
}

/**
 * Helper: safe rendered title text
 */
function getRenderedTitle(s: Speaker): string {
  return (s.title?.rendered || '').toString();
}

/**
 * Helper: Transform raw WordPress speaker data to use relative links
 */
function transformSpeaker(speaker: Speaker): Speaker {
  const slug = speaker.slug || utils.stripHTML(speaker.title?.rendered || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
    
  return {
    ...speaker,
    link: `/speaker/${slug}`,
    // Also clear out guid to avoid leaking backend URL
    guid: { rendered: '' }
  };
}

// Main API functions
export const wpApi = {
  // Test API connection
  testConnection: async (): Promise<any> => {
    try {
      const response: AxiosResponse<any> = await apiClient.get('/');
      console.log('WordPress API connection successful:', response.data);
      return response.data;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('WordPress API connection failed:', msg);
      throw error;
    }
  },

  // Get all speakers metadata for dropdown filter options (unfiltered)
  getSpeakersMetadata: async (): Promise<SpeakerMetadata[]> => {
    try {
      // During react-snap, use local snapshot so /browse can prerender reliably
      let speakers: Speaker[] = [];

      if (isPrerender()) {
        speakers = await loadPrerenderJson<Speaker[]>('speakers');
      } else {
        const response: AxiosResponse<Speaker[]> = await apiClient.get('/new-speakers', {
          params: {
            per_page: 100,
            orderby: 'title',
            order: 'asc',
          },
        });
        speakers = response.data || [];
      }

      const metadata: SpeakerMetadata[] = (speakers || []).map((speaker) => ({
        id: speaker.id,
        name: utils.stripHTML(speaker.title?.rendered || ''),
        company: utils.getACFField(speaker, 'company') || '',
        topic: utils.getACFField(speaker, 'topic') || '',
        slug: (speaker as any).slug,
      }));

      return metadata;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(`❌ [DEBUG] Error fetching speakers metadata: ${msg}`);
      if (isPrerender()) return [];
      throw error;
    }
  },

  // Get speakers with server-side filtering and pagination
  getSpeakers: async (params: SpeakersQueryParams = {}): Promise<PaginatedResponse<Speaker>> => {
    try {
      const { page = 1, perPage = 10, search, company, topic } = params;

      // During react-snap, do client-side filtering over the local snapshot
      if (isPrerender()) {
        const all = await loadPrerenderJson<Speaker[]>('speakers');

        const filtered = (all || []).filter((s) => {
          const name = utils.stripHTML(getRenderedTitle(s)).toLowerCase();
          const qSearch = (search || '').toLowerCase().trim();
          if (qSearch && !name.includes(qSearch)) return false;

          const sCompany = (utils.getACFField(s, 'company') || '').toString().toLowerCase().trim();
          const qCompany = (company || '').toLowerCase().trim();
          if (qCompany && sCompany !== qCompany) return false;

          const sTopic = utils.getACFField(s, 'topic') as any;
          const qTopic = (topic || '').toString();
          if (qTopic && !topicMatches(sTopic, qTopic)) return false;

          return true;
        });

        // Sort by title asc (matches your default server behavior)
        filtered.sort((a, b) => {
          const at = utils.stripHTML(getRenderedTitle(a)).toLowerCase();
          const bt = utils.stripHTML(getRenderedTitle(b)).toLowerCase();
          if (at < bt) return -1;
          if (at > bt) return 1;
          return 0;
        });

        const totalItems = filtered.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
        const start = (page - 1) * perPage;
        const data = filtered.slice(start, start + perPage).map(transformSpeaker);

        return {
          data,
          totalPages,
          totalItems,
          currentPage: page,
          perPage,
        };
      }

      // Normal runtime: server-side filtering via WP params
      const queryParams: any = {
        page,
        per_page: perPage,
        orderby: 'title',
        order: 'asc',
      };

      if (search) queryParams.search = search;
      if (company) queryParams.company = company;
      if (topic) queryParams.topic = topic;

      const response: AxiosResponse<Speaker[]> = await apiClient.get('/new-speakers', {
        params: queryParams,
      });

      const totalItems = parseInt(response.headers['x-wp-total'] || '0', 10);
      const totalPages = parseInt(response.headers['x-wp-totalpages'] || '1', 10);

      return {
        data: response.data.map(transformSpeaker),
        totalPages,
        totalItems,
        currentPage: page,
        perPage,
      };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(`❌ [DEBUG] Error fetching filtered speakers: ${msg}`);
      if (isPrerender()) {
        return {
          data: [],
          totalPages: 1,
          totalItems: 0,
          currentPage: 1,
          perPage: 10
        };
      }
      throw error;
    }
  },

  // Get homepage speakers only
  getHomepageSpeakers: async (): Promise<Speaker[]> => {
    try {
      let speakers: Speaker[] = [];

      if (isPrerender()) {
        speakers = await loadPrerenderJson<Speaker[]>('speakers');
      } else {
        console.log('Fetching homepage speakers from:', `${API_URL}/new-speakers`);
        const response: AxiosResponse<Speaker[]> = await apiClient.get('/new-speakers', {
          params: {
            per_page: 100,
            orderby: 'menu_order',
            order: 'asc',
          },
        });
        speakers = response.data || [];
      }

      // Only those explicitly enabled for homepage slider
      const enabled = (speakers || []).filter((s) => (s as any).use_as_homepage_slider === 'yes');

      // Sort: first by homepage_slider_order (1-7), then menu_order, then title
      const sorted = enabled.sort((a: any, b: any) => {
        const aSlider = typeof a.homepage_slider_order === 'number' ? a.homepage_slider_order : 0;
        const bSlider = typeof b.homepage_slider_order === 'number' ? b.homepage_slider_order : 0;

        const aHas = aSlider > 0;
        const bHas = bSlider > 0;
        if (aHas && bHas && aSlider !== bSlider) return aSlider - bSlider;
        if (aHas && !bHas) return -1;
        if (!aHas && bHas) return 1;

        const aMenu = typeof a.menu_order === 'number' ? a.menu_order : 0;
        const bMenu = typeof b.menu_order === 'number' ? b.menu_order : 0;
        if (aMenu !== bMenu) return aMenu - bMenu;

        const aTitle = (a.title?.rendered || '').toLowerCase();
        const bTitle = (b.title?.rendered || '').toLowerCase();
        if (aTitle < bTitle) return -1;
        if (aTitle > bTitle) return 1;
        return 0;
      });

      console.log(
          'Homepage speakers (enabled/sorted):',
          sorted.map((s) => ({
            id: s.id,
            name: s.title?.rendered,
            sliderOrder: (s as any).homepage_slider_order,
            menuOrder: (s as any).menu_order,
          }))
      );

      return sorted.map(transformSpeaker);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching homepage speakers: ${msg}`);
      if (isPrerender()) return [];
      throw error;
    }
  },

  // Get a single speaker by ID
  getSpeakerById: async (id: number | string): Promise<Speaker> => {
    try {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      if (Number.isNaN(numericId)) {
        throw new Error(`Invalid speaker id: ${id}`);
      }
      console.log('Fetching speaker by id from:', `${API_URL}/new-speakers/${numericId}`);
      const response: AxiosResponse<Speaker> = await apiClient.get(`/new-speakers/${numericId}`);
      return transformSpeaker(response.data);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching speaker by id: ${msg}`);
      throw error;
    }
  },

  // Get a single speaker by slug
  getSpeakerBySlug: async (slug: string): Promise<Speaker> => {
    try {
      if (!slug) {
        throw new Error('Invalid speaker slug');
      }

      if (isPrerender()) {
        const speakers = await loadPrerenderJson<Speaker[]>('speakers');
        const match = (speakers || []).find((s: any) => s.slug === slug);
        if (!match) throw new Error('Speaker not found');
        return transformSpeaker(match);
      }

      const response: AxiosResponse<Speaker[]> = await apiClient.get('/new-speakers', {
        params: { slug },
      });

      const [speaker] = response.data || [];
      if (!speaker) {
        throw new Error('Speaker not found');
      }
      return transformSpeaker(speaker);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching speaker by slug: ${msg}`);
      throw error;
    }
  },

  // Get featured content for homepage
  getFeaturedContent: async (): Promise<WordPressPost | null> => {
    try {
      let posts: WordPressPost[] = [];

      if (isPrerender()) {
        posts = await loadPrerenderJson<WordPressPost[]>('posts');
      } else {
        console.log('Fetching featured content from:', `${API_URL}/new-posts`);
        const response: AxiosResponse<WordPressPost[]> = await apiClient.get('/new-posts', {
          params: {
            per_page: 10,
            orderby: 'date',
            order: 'desc',
          },
        });
        posts = response.data || [];
      }

      if (!posts || posts.length === 0) {
        console.log('No featured content found');
        return null;
      }

      // Sort by date (most recent first) and take the first one
      const sortedPosts = posts.sort((a, b) => {
        const dateA = new Date(a.date || a.modified || 0).getTime();
        const dateB = new Date(b.date || b.modified || 0).getTime();
        return dateB - dateA;
      });

      const featuredPost = sortedPosts[0];

      console.log('Featured content data (most recent):', {
        id: featuredPost.id,
        title: featuredPost.title?.rendered,
        date: featuredPost.date,
        totalPosts: posts.length,
      });

      return featuredPost;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching featured content: ${msg}`);
      return null;
    }
  },
};

// Utility functions to extract data from WordPress posts
export const utils = {
  // Get custom field value (ACF fields)
  getACFField: (post: WordPressPost, fieldName: string): any => {
    return post.acf?.[fieldName] || '';
  },

  // Clean HTML tags from content
  stripHTML: (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  },

  // Decode HTML entities (e.g., &#8217; &amp; &hellip;)
  decodeHTML: (html: string): string => {
    if (!html) return '';
    const txt = typeof window !== 'undefined' ? window.document.createElement('textarea') : null;
    if (txt) {
      txt.innerHTML = html;
      return txt.value;
    }
    // Fallback: best-effort replacements on server
    return html
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&hellip;|&#8230;/g, '…');
  },

  // Remove source domain from text/html
  cleanSourceDomain: (text: string): string => {
    if (!text) return '';
    return text.replace(/https?:\/\/(?:www\.)?ravespeakers\.com/gi, '');
  },

  // Extract YouTube video ID and timestamp from various URL formats
  extractYouTubeVideoId: (url: string): string | null => {
    const result = utils.extractYouTubeData(url);
    return result ? result.videoId : null;
  },

  // Extract YouTube video data including ID and timestamp
  extractYouTubeData: (url: string): { videoId: string; startTime?: number } | null => {
    if (!url || typeof url !== 'string') return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
    ];

    let videoId: string | null = null;

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        videoId = match[1];
        break;
      }
    }

    if (!videoId) return null;

    // Extract timestamp parameter
    let startTime: number | undefined;

    const timestampPatterns = [
      /[?&]t=(\d+)s?/,
      /[?&]t=(\d+)m(\d+)?s?/,
      /[?&]t=(\d+)h(\d+)?m?(\d+)?s?/,
      /[?&]start=(\d+)/,
    ];

    for (const pattern of timestampPatterns) {
      const match = url.match(pattern);
      if (match) {
        if (pattern.source.includes('h')) {
          const hours = parseInt(match[1]) || 0;
          const minutes = parseInt(match[2]) || 0;
          const seconds = parseInt(match[3]) || 0;
          startTime = hours * 3600 + minutes * 60 + seconds;
        } else if (pattern.source.includes('m')) {
          const minutes = parseInt(match[1]) || 0;
          const seconds = parseInt(match[2]) || 0;
          startTime = minutes * 60 + seconds;
        } else {
          startTime = parseInt(match[1]) || 0;
        }
        break;
      }
    }

    return { videoId, startTime };
  },
};
